import React from 'react';
import { useT } from '../i18n';

export interface RiContract {
  id: number;
  start: string;
  end: string | null;
  label: string;
}

export interface RiCycle {
  cycle_number: number;
  contracts: RiContract[];
  next_measure_type: string | null;
}

export interface RiTimelineData {
  cycles: RiCycle[];
}

interface Props {
  timeline: RiTimelineData;
  currentTaux: number | null;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const W = 1000;
const HALF = W / 2;       // x = M6
const TRACK_Y = 36;
const TRACK_H = 14;
const SVG_H = 92;

// ── Helpers ──────────────────────────────────────────────────────────────────

function toMs(iso: string): number {
  return new Date(iso.slice(0, 10) + 'T00:00:00Z').getTime();
}

function daysBetween(a: string, b: string): number {
  return Math.round((toMs(b) - toMs(a)) / 86400000);
}

/** Budget months consumed within a cycle (pause excluded). Not capped. */
function dateToBudgetMonths(today: string, cycle: RiCycle): number {
  const c1 = cycle.contracts[0];
  if (!c1) return 0;
  const c2 = cycle.contracts[1] ?? null;
  const todayMs = toMs(today);
  const c1EndMs = c1.end ? toMs(c1.end) : null;

  if (!c1EndMs || todayMs <= c1EndMs) {
    return Math.max(0, (todayMs - toMs(c1.start)) / (30.44 * 86400000));
  }
  if (!c2 || todayMs < toMs(c2.start)) return 6;
  return 6 + (todayMs - toMs(c2.start)) / (30.44 * 86400000);
}

/** Map budget months to SVG X (capped to W). */
function mx(months: number): number {
  return Math.max(0, Math.min(W, (months / 12) * W));
}

type CyclePhase = 'c1' | 'pause' | 'c2' | 'done';

function getCyclePhase(today: string, cycle: RiCycle): CyclePhase {
  const c1 = cycle.contracts[0];
  if (!c1) return 'c1';
  const c2 = cycle.contracts[1] ?? null;
  const todayMs = toMs(today);
  const c1EndMs = c1.end ? toMs(c1.end) : null;
  if (!c1EndMs || todayMs <= c1EndMs) return 'c1';
  if (!c2 || todayMs < toMs(c2.start)) return 'pause';
  if (c2.end && todayMs > toMs(c2.end)) return 'done';
  return 'c2';
}

// ── Sub-component: one cycle ──────────────────────────────────────────────────

function CycleSVG({ cycle, today }: { cycle: RiCycle; today: string }) {
  const { t } = useT();
  const c1 = cycle.contracts[0];
  const c2 = cycle.contracts[1] ?? null;
  const phase = getCyclePhase(today, cycle);
  const budgetMonths = dateToBudgetMonths(today, cycle);
  const todayX = mx(Math.min(budgetMonths, 12));

  // Pause durations
  const pauseDays = c1?.end && c2?.start ? daysBetween(c1.end, c2.start) : null;
  const pausedDays = phase === 'pause' && c1?.end ? daysBetween(c1.end, today) : null;

  // Fill widths
  const c1FillW = phase === 'c1'
    ? Math.min(todayX, HALF)
    : HALF;
  const c2FillW = phase === 'c2'
    ? Math.max(0, Math.min(todayX - HALF, HALF))
    : phase === 'done'
      ? HALF
      : 0;

  // Approaching-end detection
  const WARN_DAYS = 30;
  const daysToC1End = c1?.end ? daysBetween(today, c1.end) : null;
  const daysToC2End = c2?.end ? daysBetween(today, c2.end) : null;
  const c1Warn = phase === 'c1' && daysToC1End != null && daysToC1End >= 0 && daysToC1End <= WARN_DAYS;
  const c2Warn = phase === 'c2' && daysToC2End != null && daysToC2End >= 0 && daysToC2End <= WARN_DAYS;

  const c1FillColor = c1Warn ? 'var(--warn)' : 'var(--accent)';
  const c2FillColor = c2Warn ? 'var(--err)' : 'color-mix(in srgb, var(--accent) 70%, var(--ok))';

  // Alert text (approaching end)
  const alertDays = c1Warn ? daysToC1End : c2Warn ? daysToC2End : null;
  const alertColor = c2Warn ? 'var(--err)' : 'var(--warn)';
  const alertLabel = alertDays != null
    ? `${t('timeline.ri.contractEndsIn', { n: alertDays })}${alertDays <= 7 ? ' !' : ''}`
    : null;
  const alertX = c2Warn
    ? Math.max(alertLabel ? alertLabel.length * 5.5 / 2 + 8 : 80, Math.min(W - 4, todayX + 30))
    : Math.min(HALF - 4, todayX + 30);

  // Pause badge text
  const pauseBadgeText = phase === 'pause'
    ? t('timeline.ri.pauseActive', { n: pausedDays ?? 0 })
    : pauseDays != null && pauseDays > 0
      ? t('timeline.ri.pauseDuration', { n: pauseDays })
      : null;

  return (
    <svg
      viewBox={`0 0 ${W} ${SVG_H}`}
      width="100%"
      style={{ display: 'block', overflow: 'visible' }}
    >
      {/* ── Contract labels above track ── */}
      <text
        x={HALF / 2} y={12}
        textAnchor="middle" fontSize={9} fontWeight={600}
        fill={c1Warn ? 'var(--warn)' : 'var(--accent)'}
        opacity={0.9}
      >
        {t('timeline.ri.contract1Label')}
      </text>
      <text
        x={HALF + HALF / 2} y={12}
        textAnchor="middle" fontSize={9} fontWeight={600}
        fill={c2 ? (c2Warn ? 'var(--err)' : 'color-mix(in srgb, var(--accent) 70%, var(--ok))') : 'var(--ink-3)'}
        opacity={c2 ? 0.9 : 0.4}
      >
        {t('timeline.ri.contract2Label')}
      </text>

      {/* ── Pause badge (centered on M6) ── */}
      {pauseBadgeText && (() => {
        const tw = pauseBadgeText.length * 5.6 + 16;
        const isPausing = phase === 'pause';
        return (
          <g>
            <rect
              x={HALF - tw / 2} y={16} width={tw} height={16} rx={4}
              fill={isPausing
                ? 'color-mix(in srgb, var(--warn) 14%, var(--surface))'
                : 'var(--surface)'}
              stroke={isPausing ? 'var(--warn)' : 'var(--line)'}
              strokeWidth={1}
            />
            <text
              x={HALF} y={27}
              textAnchor="middle" fontSize={9} fontWeight={600}
              fill={isPausing ? 'var(--warn)' : 'var(--ink-3)'}
            >
              {pauseBadgeText}
            </text>
          </g>
        );
      })()}

      {/* ── M6 separator (within + just around track) ── */}
      <line
        x1={HALF} y1={TRACK_Y - 4}
        x2={HALF} y2={TRACK_Y + TRACK_H + 10}
        stroke="var(--line-strong)" strokeWidth={1.5}
      />

      {/* ── Background track ── */}
      <rect
        x={0} y={TRACK_Y} width={W} height={TRACK_H} rx={TRACK_H / 2}
        fill="color-mix(in srgb, var(--line) 80%, var(--bg))"
      />

      {/* ── Left half outline (C1 block boundary) ── */}
      <rect
        x={0} y={TRACK_Y} width={HALF} height={TRACK_H}
        fill="none" stroke="var(--accent)" strokeWidth={1} opacity={0.15}
      />

      {/* ── Right half outline (C2 block boundary) ── */}
      <rect
        x={HALF} y={TRACK_Y} width={HALF} height={TRACK_H}
        fill="none"
        stroke={c2 ? 'color-mix(in srgb, var(--accent) 70%, var(--ok))' : 'var(--ink-3)'}
        strokeWidth={1} opacity={c2 ? 0.15 : 0.1}
        strokeDasharray={c2 ? undefined : '6 3'}
      />

      {/* ── C1 fill ── */}
      {c1FillW > 0 && (
        <rect
          x={0} y={TRACK_Y} width={c1FillW} height={TRACK_H}
          fill={c1FillColor} opacity={0.65}
        />
      )}

      {/* ── C2 fill ── */}
      {c2FillW > 0 && (
        <rect
          x={HALF} y={TRACK_Y} width={c2FillW} height={TRACK_H}
          fill={c2FillColor} opacity={0.65}
        />
      )}

      {/* ── Today indicator ── */}
      {(phase === 'c1' || phase === 'c2') && (
        <g>
          <line
            x1={todayX} y1={TRACK_Y - 6}
            x2={todayX} y2={TRACK_Y + TRACK_H + 6}
            stroke="var(--accent)" strokeWidth={2}
          />
          <polygon
            points={`${todayX - 4},${TRACK_Y - 6} ${todayX + 4},${TRACK_Y - 6} ${todayX},${TRACK_Y - 1}`}
            fill="var(--accent)"
          />
        </g>
      )}

      {/* ── Month ticks + labels ── */}
      {[0, 6, 12].map(m => {
        const x = mx(m);
        return (
          <g key={m}>
            <line
              x1={x} y1={TRACK_Y + TRACK_H}
              x2={x} y2={TRACK_Y + TRACK_H + 7}
              stroke="var(--ink-3)" strokeWidth={1}
            />
            <text
              x={x} y={TRACK_Y + TRACK_H + 17}
              textAnchor="middle" fontSize={10}
              fill={m === 0 || m === 12 ? 'var(--ink-2)' : 'var(--ink-3)'}
              fontWeight={m === 0 || m === 12 ? 600 : 400}
            >
              M{m}
            </text>
          </g>
        );
      })}

      {/* ── Alert badge ── */}
      {alertLabel && (() => {
        const tw = alertLabel.length * 5.5 + 16;
        const bx = Math.max(tw / 2 + 4, Math.min(W - tw / 2 - 4, alertX));
        return (
          <g>
            <rect
              x={bx - tw / 2} y={TRACK_Y + TRACK_H + 22}
              width={tw} height={16} rx={4}
              fill={`color-mix(in srgb, ${alertColor} 12%, var(--surface))`}
              stroke={alertColor} strokeWidth={1}
            />
            <text
              x={bx} y={TRACK_Y + TRACK_H + 33}
              textAnchor="middle" fontSize={9} fontWeight={700}
              fill={alertColor}
            >
              {alertLabel}
            </text>
          </g>
        );
      })()}

      {/* ── Next measure after M12 ── */}
      {cycle.next_measure_type && (phase === 'done' || budgetMonths > 11) && (
        <text
          x={W - 4} y={TRACK_Y + TRACK_H / 2 + 4}
          textAnchor="end" fontSize={10} fill="var(--ink-2)" fontWeight={500}
        >
          → {cycle.next_measure_type}
        </text>
      )}
    </svg>
  );
}

// ── Legend item ───────────────────────────────────────────────────────────────

function LegendItem({ color, label, opacity }: { color: string; label: string; opacity?: number }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{
        width: 18, height: 8, borderRadius: 2,
        display: 'inline-block', background: color, opacity: opacity ?? 1,
      }} />
      {label}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function RiTimeline({ timeline, currentTaux }: Props) {
  const { t } = useT();
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 'var(--s8)', gap: 'var(--s8)', flexWrap: 'wrap',
      }}>
        <span style={{
          fontSize: 'var(--t-12)', fontWeight: 600,
          color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          {t('timeline.ri.title')}
        </span>
        {currentTaux != null && (
          <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>
            {t('timeline.ri.tauxActivite')} <strong style={{ color: 'var(--ink-1)' }}>{currentTaux} %</strong>
          </span>
        )}
      </div>

      {/* Cycles */}
      {timeline.cycles.map((cycle, i) => {
        const budgetMonths = dateToBudgetMonths(today, cycle);
        const budgetRounded = Math.floor(budgetMonths * 10) / 10;
        const isCurrentCycle = i === timeline.cycles.length - 1;

        return (
          <div
            key={cycle.cycle_number}
            style={{ marginBottom: i < timeline.cycles.length - 1 ? 'var(--s16)' : 0 }}
          >
            {/* Cycle header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 'var(--s8)',
              marginBottom: 'var(--s4)', flexWrap: 'wrap',
            }}>
              {timeline.cycles.length > 1 && (
                <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', fontWeight: 500 }}>
                  {t('timeline.ri.cycle', { n: cycle.cycle_number })}
                </span>
              )}
              {isCurrentCycle && (
                <span style={{
                  padding: '2px 10px',
                  borderRadius: 'var(--r2)',
                  fontWeight: 700,
                  fontSize: 'var(--t-11)',
                  background: budgetMonths >= 11
                    ? 'color-mix(in srgb, var(--err) 12%, var(--surface))'
                    : budgetMonths >= 9
                      ? 'color-mix(in srgb, var(--warn) 14%, var(--surface))'
                      : 'color-mix(in srgb, var(--accent) 12%, var(--surface))',
                  color: budgetMonths >= 11
                    ? 'var(--err)'
                    : budgetMonths >= 9
                      ? 'var(--warn)'
                      : 'var(--accent)',
                }}>
                  {t('timeline.measure.budgetLabel', { month: budgetRounded, budget: 12 })}
                </span>
              )}
              {!isCurrentCycle && (
                <span style={{
                  padding: '2px 10px', borderRadius: 'var(--r2)',
                  fontSize: 'var(--t-11)', fontWeight: 500,
                  background: 'color-mix(in srgb, var(--ok) 10%, var(--surface))',
                  color: 'var(--ok)',
                }}>
                  {t('timeline.ri.done')}
                </span>
              )}
            </div>

            <CycleSVG cycle={cycle} today={today} />
          </div>
        );
      })}

      {/* Legend */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 'var(--s12)',
        fontSize: 'var(--t-11)', color: 'var(--ink-3)', marginTop: 'var(--s6)',
      }}>
        <LegendItem color="var(--accent)" label={t('timeline.ri.legendContract1')} opacity={0.65} />
        <LegendItem
          color="color-mix(in srgb, var(--accent) 70%, var(--ok))"
          label={t('timeline.ri.legendContract2')}
          opacity={0.65}
        />
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <svg width={16} height={10} viewBox="0 0 16 10">
            <line x1={8} y1={0} x2={8} y2={10} stroke="var(--accent)" strokeWidth={2} />
            <polygon points="4,0 12,0 8,5" fill="var(--accent)" />
          </svg>
          {t('timeline.legend.today')}
        </span>
      </div>
    </div>
  );
}
